interface RandomEntry {
  value: number;
  timestamp: number;
}

const randomNumbersMap = new Map<string, RandomEntry>();
const UPDATE_INTERVAL_MS = 60_000 * 60;

/** Returns an integer between min and max (inclusive). */
function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Clamps value to the [min, max] range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Generates (and caches) a “random” number for a given key.
 * • If no key is passed, always returns a fresh random int.
 * • If called again for the same key within UPDATE_INTERVAL_MS, returns the cached value.
 * • Otherwise, updates by a ±1–10% change (or initializes if first time).
 */
export function generateRandomNumber(key?: string, min = 0, max = 1000): number {
  const now = Date.now();

  // no key → always new
  if (!key) {
    return getRandomInt(min, max);
  }

  const entry = randomNumbersMap.get(key);

  // still fresh: return cached
  if (entry && now - entry.timestamp < UPDATE_INTERVAL_MS) {
    return entry.value;
  }

  // compute new value
  let newValue: number;
  if (!entry) {
    // first time for this key
    newValue = getRandomInt(min, max);
  } else {
    // expired → adjust by ±1–10%
    const direction = Math.random() < 0.5 ? -1 : 1;
    const pct = (Math.random() * 9 + 1) / 100;
    newValue = Math.round(entry.value * (1 + direction * pct));
    newValue = clamp(newValue, min, max);
  }

  randomNumbersMap.set(key, { value: newValue, timestamp: now });
  return newValue;
}
